"use client";

import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar } from "@/components/common/Avatar";
import { getNavItems } from "./navData";
import { useEffect, useRef, useState } from "react";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * 移动端抽屉侧栏
 * - 遮罩层
 * - 左侧滑入的导航菜单
 */
export function MobileDrawer({ isOpen, onClose }: MobileDrawerProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, token, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const navItems = getNavItems(isAdmin);
  const prevPathnameRef = useRef(pathname);

  // 徽章状态
  const [hasActiveTasks, setHasActiveTasks] = useState(false);
  const [runningPluginsCount, setRunningPluginsCount] = useState(0);
  const [runningProjectsCount, setRunningProjectsCount] = useState(0);

  // 定期检查活跃任务
  useEffect(() => {
    if (!token) return;
    const checkActiveTasks = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/scheduled-tasks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const statusCounts = data.status_counts || {};
          const activeCount = (statusCounts.pending || 0) + (statusCounts.running || 0);
          setHasActiveTasks(activeCount > 0);
        }
      } catch {
        setHasActiveTasks(false);
      }
    };
    checkActiveTasks();
    const interval = setInterval(checkActiveTasks, 15000);
    return () => clearInterval(interval);
  }, [token]);

  // 定期检查运行中的插件
  useEffect(() => {
    if (!token) return;
    const checkRunningPlugins = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/plugins`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const plugins = data.data || [];
          const runningCount = plugins.filter((p: { running?: boolean }) => p.running).length;
          setRunningPluginsCount(runningCount);
        }
      } catch {
        setRunningPluginsCount(0);
      }
    };
    checkRunningPlugins();
    const interval = setInterval(checkRunningPlugins, 15000);
    return () => clearInterval(interval);
  }, [token]);

  // 定期检查运行中的项目
  useEffect(() => {
    if (!token) return;
    const checkRunningProjects = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/running`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const runningIds = data.running_project_ids || [];
          setRunningProjectsCount(runningIds.length);
        }
      } catch {
        setRunningProjectsCount(0);
      }
    };
    checkRunningProjects();
    const interval = setInterval(checkRunningProjects, 15000);
    return () => clearInterval(interval);
  }, [token]);

  // 路由变化时关闭抽屉（仅在 pathname 实际变化时）
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
      onClose();
    }
  }, [pathname, onClose]);

  // ESC 键关闭
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      // 禁止背景滚动
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  const handleLogout = async () => {
    await logout();
    router.push("/login");
    onClose();
  };

  return (
    <>
      {/* 遮罩层 */}
      <div
        className={`
          fixed inset-0 bg-black/50 z-40 lg:hidden
          transition-opacity duration-300
          ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}
        `}
        onClick={onClose}
      />

      {/* 抽屉内容 */}
      <div
        className="fixed left-0 z-50 w-64 bg-[#1a1a1a] border-r border-[#2a2a2a] lg:hidden safe-area-left flex flex-col"
        style={{
          top: "env(safe-area-inset-top, 0px)",
          bottom: 0,
          transform: isOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 300ms ease-in-out"
        }}
      >
        {/* 头部 */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-[#2a2a2a] flex-shrink-0">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <svg
              className="w-8 h-8"
              viewBox="0 0 100 100"
              style={{
                filter: "drop-shadow(0 0 8px rgba(249, 115, 22, 0.6))",
              }}
            >
              <defs>
                <linearGradient
                  id="drawerGradOrange"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop
                    offset="0%"
                    style={{ stopColor: "#f97316", stopOpacity: 1 }}
                  />
                  <stop
                    offset="100%"
                    style={{ stopColor: "#eab308", stopOpacity: 1 }}
                  />
                </linearGradient>
              </defs>
              <path
                d="M 75 50 A 25 25 0 1 1 50 25 L 50 35 A 15 15 0 1 0 65 50 Z"
                fill="url(#drawerGradOrange)"
                opacity="0.95"
              />
            </svg>
            <span className="text-[#e5e5e5] font-semibold">SuperOne</span>
          </div>

          {/* 关闭按钮 */}
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-[#2a2a2a] transition-colors"
            aria-label="关闭菜单"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-[#808080]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* 导航项 */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 mobile-scroll min-h-0">
          {navItems.map((item) => {
            const isActive =
              pathname === item.href || pathname.startsWith(item.href + "/");
            return (
              <button
                key={item.href}
                onClick={() => {
                  router.push(item.href);
                }}
                className={`
                  w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-colors
                  ${
                    isActive
                      ? "bg-[#3a3a3a] text-[#e5e5e5]"
                      : "text-[#808080] hover:bg-[#2a2a2a] hover:text-[#e5e5e5]"
                  }
                `}
              >
                {/* 图标容器 - 用于放置徽章 */}
                <div className="relative">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={item.icon}
                    />
                  </svg>
                  {/* 活跃任务指示灯 - 绿色脉冲点 */}
                  {item.hasHealthIndicator && hasActiveTasks && (
                    <span
                      className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full animate-pulse"
                      style={{
                        backgroundColor: "#22c55e",
                        boxShadow: "0 0 6px 2px rgba(34, 197, 94, 0.6)"
                      }}
                    />
                  )}
                  {/* 运行中插件数量徽章 - 紫色 */}
                  {item.hasPluginsBadge && runningPluginsCount > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 text-[10px] font-medium rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: "#9333ea",
                        color: "#ffffff",
                        boxShadow: "0 0 6px 2px rgba(147, 51, 234, 0.5)"
                      }}
                    >
                      {runningPluginsCount > 99 ? "99+" : runningPluginsCount}
                    </span>
                  )}
                  {/* 运行中项目数量徽章 - 橙色 */}
                  {item.hasProjectsBadge && runningProjectsCount > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 text-[10px] font-medium rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: "#f97316",
                        color: "#ffffff",
                        boxShadow: "0 0 6px 2px rgba(249, 115, 22, 0.5)"
                      }}
                    >
                      {runningProjectsCount > 99 ? "99+" : runningProjectsCount}
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* 用户区域 - 贴底 */}
        {user && (
          <div className="border-t border-[#2a2a2a] p-4 flex-shrink-0 safe-area-bottom">
            <div className="flex items-center gap-3 mb-3">
              <Avatar
                src={user.avatar}
                name={user.display_name || user.username}
                size="md"
                style={{ backgroundColor: "#4a4a4a", color: "#ffffff" }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#e5e5e5] font-medium truncate">
                  {user.display_name || user.username}
                </div>
                <div className="text-xs text-[#808080] truncate">
                  @{user.username}
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-[#e5e5e5] hover:bg-[#2a2a2a] transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              退出登录
            </button>
          </div>
        )}
      </div>
    </>
  );
}
